import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Search, RefreshCw, Usb, User } from "lucide-react";
import { toast } from "../../utils/toast";
import { useTeamFilter } from "../../utils/useTeamFilter";
import "./usb.css";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const UsbControl = () => {
  const navigate = useNavigate();
  const [usbData, setUsbData] = useState([]);
  const [selectedUsb, setSelectedUsb] = useState(null);
  const [loading, setLoading] = useState(false);
  const { departments, getTeamName } = useTeamFilter();
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchUsbData = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/usb`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      setUsbData(data);
    } catch (err) {
      console.error("Failed to fetch USB data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsbData();
  }, []);

  // Team filtering happens here, at the per-employee level, before devices are
  // deduped — it answers "which team's devices". Name search is applied
  // separately below, against the deduped device list itself, since the cards
  // now show the pendrive's name/vendor/serial, not an employee name.
  const getUniqueDevices = () => {
    const map = new Map();
    const teamFilteredData = selectedTeam === "all"
      ? usbData
      : usbData.filter((agent) => getTeamName(agent.agentId) === selectedTeam);
    teamFilteredData.forEach((agent) => {
      agent.data?.connected_devices?.forEach((device) => {
        if (!map.has(device.serial_number)) {
          map.set(device.serial_number, {
            ...device,
            users: [],
          });
        }
        map.get(device.serial_number).users.push({
          username: agent.agentId,
          status: device.status,
        });
      });
    });
    return Array.from(map.values());
  };

  const uniqueDevices = getUniqueDevices();
  const q = searchTerm.trim().toLowerCase();
  const filteredDevices = !q
    ? uniqueDevices
    : uniqueDevices.filter((device) =>
        (device.description || "").toLowerCase().includes(q) ||
        (device.vendor_id || "").toLowerCase().includes(q) ||
        (device.serial_number || "").toLowerCase().includes(q)
      );

  // Handle status change for a specific user and device
  const handleStatusChange = async (username, serial, newStatus) => {
    try {
      const token = sessionStorage.getItem("token");
      await fetch(`${BACKEND_URL}/api/usb/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ username, serial, status: newStatus }),
      });

      // Update local state
      setUsbData((prev) =>
        prev.map((agent) =>
          agent.agentId === username
            ? {
              ...agent,
              data: {
                ...agent.data,
                connected_devices: agent.data.connected_devices.map((d) =>
                  d.serial_number === serial ? { ...d, status: newStatus } : d
                ),
              },
            }
            : agent
        )
      );
      toast.success(`Access policy updated: ${newStatus} for ${username}`);
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error("Policy Update Failed: Could not synchronize with endpoint.");
    }
  };

  if (loading) {
    return (
      <div className="usb-content-wrapper">
        <p className="loading">Loading USB data...</p>
      </div>
    );
  }

  // Render USB device list — one card per pendrive, not per username, mirroring
  // File Upload's agent-card grid but keyed off the device instead of the employee.
  const renderDeviceList = () =>
    filteredDevices.length === 0 ? (
      <div className="empty-state">
        <Usb size={48} />
        <p>No USB devices found</p>
      </div>
    ) : (
      <div className="agents-grid">
        {filteredDevices.map((device) => (
          <div
            key={device.serial_number}
            className="agent-card"
            onClick={() => setSelectedUsb(device.serial_number)}
          >
            <div className="agent-icon">
              <User size={24} />
            </div>
            <div className="agent-info">
              <h3>{device.description || "Unknown Device"}</h3>
              <p className="agent-hostname">
                {device.vendor_id || "Unknown vendor"} &middot; {device.serial_number}
              </p>
              <div className="agent-counts">
                <span className="count-users">{device.users.length} user{device.users.length !== 1 ? "s" : ""}</span>
                <span className="count-lastseen">Last seen: {new Date(device.last_seen).toLocaleString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );

  // Render user access for selected USB
  const renderUserAccess = () => {
    const device = uniqueDevices.find((d) => d.serial_number === selectedUsb);
    if (!device) return null;

    return (
      <div className="usb-user-details">
        <button className="gc-icon-btn" onClick={() => setSelectedUsb(null)} data-tip="Back to USB List" aria-label="Back to USB List">
          <ArrowLeft size={16} />
        </button>

        <div className="usb-device-title">
          <span className="usb-device-label">Device</span>
          <span className="usb-device-name">{device.description}</span>
          <span className="usb-device-serial">{device.serial_number}</span>
        </div>

        {/* Desktop table */}
        <div className="usb-access-table-wrap">
          <table className="usb-table usb-access-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Last Seen</th>
                <th>Access Policy</th>
              </tr>
            </thead>
            <tbody>
              {device.users.map((user, idx) => {
                const agentDevice = usbData
                  .find((a) => a.agentId === user.username)
                  ?.data?.connected_devices?.find((d) => d.serial_number === selectedUsb);
                const statusClass = user.status === "Allowed" ? "allowed" : user.status === "Blocked" ? "blocked" : "waiting";
                return (
                  <tr key={idx}>
                    <td className="usb-username">{user.username}</td>
                    <td className="usb-lastseen">{agentDevice ? new Date(agentDevice.last_seen).toLocaleString() : "—"}</td>
                    <td>
                      <select
                        className={`status-select ${statusClass}`}
                        value={user.status}
                        onChange={(e) => handleStatusChange(user.username, selectedUsb, e.target.value)}
                      >
                        <option value="Allowed">Allowed</option>
                        <option value="Blocked">Blocked</option>
                        <option value="WaitingForApproval">Waiting For Approval</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="usb-access-cards">
          {device.users.map((user, idx) => {
            const agentDevice = usbData
              .find((a) => a.agentId === user.username)
              ?.data?.connected_devices?.find((d) => d.serial_number === selectedUsb);
            const statusClass = user.status === "Allowed" ? "allowed" : user.status === "Blocked" ? "blocked" : "waiting";
            return (
              <div key={idx} className="usb-access-card">
                <div className="usb-card-top">
                  <span className="usb-card-username">{user.username}</span>
                  <span className={`usb-card-badge ${statusClass}`}>{user.status === "WaitingForApproval" ? "Pending" : user.status}</span>
                </div>
                {agentDevice && (
                  <div className="usb-card-lastseen">
                    Last seen: {new Date(agentDevice.last_seen).toLocaleString()}
                  </div>
                )}
                <select
                  className={`status-select ${statusClass}`}
                  value={user.status}
                  onChange={(e) => handleStatusChange(user.username, selectedUsb, e.target.value)}
                >
                  <option value="Allowed">Allowed</option>
                  <option value="Blocked">Blocked</option>
                  <option value="WaitingForApproval">Waiting For Approval</option>
                </select>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="usb-content-wrapper">
      <header className="usb-header">
        <p>
          {selectedUsb
            ? "View users and update their access statuses."
            : "Click a USB device to see users who have access."}
        </p>
        {!selectedUsb && (
          <button className="gc-icon-btn" data-tip="Refresh" onClick={fetchUsbData} disabled={loading}>
            <RefreshCw size={14} />
          </button>
        )}
      </header>

      {!selectedUsb && (
        <div className="team-filter-row">
          <div className="name-search-wrapper">
            <Search size={16} className="team-filter-icon" />
            <input
              type="text"
              placeholder="Search by device name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {departments.length > 0 && (
            <div className="team-select-wrapper">
              <Users size={16} className="team-filter-icon" />
              <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
                <option value="all">All Teams</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept.name}>{dept.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
      {selectedUsb ? renderUserAccess() : renderDeviceList()}
    </div>
  );
};

export default UsbControl;
